import React, { useContext } from "react";
import { NavLink, Outlet, useLocation, Navigate } from "react-router-dom";
import { GlobalContext } from "../../context/globalContext";
import { logoutUserAPI } from "../../api/auth";
import { isSuperAdminRole } from "../../api/admin";

const MAIN_NAV_ITEMS: { to: string; label: string; end?: boolean }[] = [
  { to: "/user/dashboard", label: "Dashboard", end: true },
  { to: "/user/orders", label: "Orders & registrations" },
  { to: "/user/account", label: "Account" },
  { to: "/user/preferences", label: "Preferences" },
];

function navClass({ isActive }: { isActive: boolean }): string {
  return `block px-3 py-2.5 text-sm rounded-sm transition-colors ${
    isActive
      ? "bg-slate-800 text-white font-medium"
      : "text-slate-700 hover:bg-slate-100"
  }`;
}

function otherNavClass({ isActive }: { isActive: boolean }): string {
  return `block px-3 py-2 text-xs rounded-sm transition-colors ${
    isActive
      ? "bg-slate-800 text-white font-medium"
      : "text-slate-600 hover:bg-slate-100"
  }`;
}

export default function UserDashboardLayout() {
  const { currentUser, setCurrentUser, isAuthLoading } =
    useContext(GlobalContext);
  const location = useLocation();
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);

  async function handleLogout() {
    try {
      setIsLoggingOut(true);
      const response = await logoutUserAPI();
      if (response?.ok) {
        setCurrentUser(null);
        window.location.href = "/";
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoggingOut(false);
    }
  }

  if (isAuthLoading) {
    return (
      <div className="eh-page py-12 text-center text-slate-500">
        Loading your account…
      </div>
    );
  }

  if (!currentUser?.email) {
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(location.pathname)}`}
        replace
      />
    );
  }

  const isEventAdminPanel = location.pathname.includes(
    "/user/event-admin/events/",
  );

  return (
    <div className="eh-page mx-auto py-8 mb-24 max-w-[1100px]">
      <div className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
          My account
        </p>
        <h1 className="text-2xl font-bold text-slate-900">
          Hello, {currentUser.firstName || "there"}
        </h1>
        <p className="text-sm text-slate-600 mt-1">{currentUser.email}</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        <aside className="lg:w-56 shrink-0">
          <nav className="eh-card p-3 sticky top-24">
            <div className="space-y-0.5">
              {MAIN_NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={navClass}
                >
                  {item.label}
                </NavLink>
              ))}
            </div>

            <div className="border-t border-slate-200 mt-3 pt-3">
              <p className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                Other
              </p>
              <div className="space-y-0.5">
                {isSuperAdminRole(currentUser.role) && (
                  <NavLink to="/user/administration" className={otherNavClass}>
                    Administration
                  </NavLink>
                )}
                {isSuperAdminRole(currentUser.role) && (
                  <NavLink to="/user/paper-videos" className={otherNavClass}>
                    Paper Videos
                  </NavLink>
                )}
                <NavLink to="/user/events" className={otherNavClass}>
                  Manage events
                </NavLink>
              </div>
            </div>

            <button
              type="button"
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="w-full text-left px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-100 rounded-sm transition-colors mt-3 border-t border-slate-100 pt-3"
            >
              {isLoggingOut ? "Signing out…" : "Sign out"}
            </button>
          </nav>
        </aside>

        <div className="flex-1 min-w-0">
          {isEventAdminPanel ? (
            <Outlet />
          ) : (
            <div className="eh-card">
              <Outlet />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
